/**
 * MessageContent - Component for rendering rich content in chat messages
 * 
 * Parses message text and renders:
 * - Markdown formatting: **bold**, *italic*, lists, etc.
 * - Standard markdown links: [text](url) → <a href="url" target="_blank">text</a>
 * - Custom button syntax: [btn:Label](url) → styled button element
 * - Button variants: [btn:Label:variant](url) → colored button element
 * 
 * Supported button variants:
 * - default: theme accent color
 * - primary: theme primary color
 * - success, accept, yes: green confirmation button
 * - danger, reject, no: red decline button
 * - warning, maybe: amber/yellow uncertain button
 * - info: blue informational button
 * - neutral: gray neutral button
 * 
 * Follows ADR principles:
 * - ADR-003: High Cohesion - Single responsibility: parse and render rich content
 * - ADR-004: Separation of Concerns - Pure presentation component
 */

import React, { useMemo, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import type { Components } from 'react-markdown';
import type { ButtonVariant } from '../types/theme';
import {
  hasOrderedListMarkdown,
  normalizeMarkdownForChat,
} from '../utils/markdownNormalize';

/**
 * Props for MessageContent component
 */
interface MessageContentProps {
  /** The raw message content to parse and render */
  content: string;
  /** Optional callback when a button is clicked */
  onButtonClick?: (url: string, label: string, variant?: ButtonVariant) => void;
  /** Whether this is a customer message (affects link styling) */
  isCustomerMessage?: boolean;
  /** Lightweight plain-text mode while the typewriter is still advancing */
  isTyping?: boolean;
}

/**
 * Represents a parsed segment of the message
 */
type ContentSegment =
  | { type: 'text'; content: string }
  | { type: 'link'; label: string; url: string }
  | { type: 'button'; label: string; url: string; variant: ButtonVariant };

/**
 * Regex pattern for matching markdown links and buttons
 * Matches: [text](url) or [btn:text](url) or [btn:text:variant](url)
 * Groups: 1=full label (may include btn: prefix and variant), 2=url
 */
const LINK_PATTERN = /\[([^\]]+)\]\(([^)]+)\)/g;

/**
 * Regex patterns for markdown detection
 * 
 * Note: These patterns use RegExp constructor with string concatenation to prevent
 * Tailwind CSS from incorrectly interpreting them as arbitrary value classes.
 * The pattern `[-:\s|]` looks like a Tailwind class to Tailwind's content scanner,
 * which generates invalid CSS like `.\[-\:\\s\|\] { -: \s|; }`.
 * 
 * By using string concatenation (e.g., '[-:\\s' + '|]'), we break up the pattern
 * so Tailwind's simple regex scanner doesn't recognize it.
 */
const TABLE_PATTERN = new RegExp('\\|.*\\|[\\s\\S]*\\|[-:\\s' + '|]+\\|');
const HR_PATTERN = new RegExp('(^|\\n)([-*_]){3,}\\s*($|\\n)');

/**
 * Pre-processes markdown for chat display.
 * - Escaped newlines become real newlines
 * - Paragraph breaks (\n\n+) become visible block gaps
 * - Single line breaks become GFM hard breaks (two trailing spaces)
 */
function preserveLineBreaks(md: string): string {
  if (!md) return md;
  const normalized = md.replace(/\\n/g, '\n');
  return normalized
    .split(/\n\n+/)
    .map(paragraph => paragraph.replace(/\n/g, '  \n'))
    .join('\n\n<br/>\n\n');
}

function containsLineBreak(text: string): boolean {
  return text.includes('\n');
}

/**
 * Extracts plain text from ReactMarkdown children for blank-code-block checks.
 */
function getReactNodeText(node: React.ReactNode): string {
  if (node == null || typeof node === 'boolean') {
    return '';
  }
  if (typeof node === 'string') {
    return node;
  }
  if (typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(getReactNodeText).join('');
  }
  if (React.isValidElement(node)) {
    const props = node.props as { children?: React.ReactNode };
    return getReactNodeText(props.children);
  }
  return '';
}

function normalizeCodeBlockText(text: string): string {
  return text
    .replace(/<br\s*\/?>/gi, '')
    .replace(/\u00a0/g, ' ')
    .trim();
}

function isBlankCodeBlock(children: React.ReactNode): boolean {
  return normalizeCodeBlockText(getReactNodeText(children)).length === 0;
}

/**
 * Detects whether text contains markdown that should be rendered.
 */
function hasMarkdownFormatting(text: string): boolean {
  const hasBasicMarkdown = /(\*\*|__|\*|_|~~|`)/.test(text);
  const hasUnorderedList = /(^|\n)- /.test(text);
  const hasOrderedList = hasOrderedListMarkdown(text);
  const hasTable = TABLE_PATTERN.test(text);
  const hasBlockquote = /(^|\n)>\s/.test(text);
  const hasHorizontalRule = HR_PATTERN.test(text);
  const hasCodeBlock = /```/.test(text);

  return (
    hasBasicMarkdown ||
    hasUnorderedList ||
    hasOrderedList ||
    hasTable ||
    hasBlockquote ||
    hasHorizontalRule ||
    hasCodeBlock
  );
}

/**
 * Normalizes button variant aliases to canonical names
 * 
 * @param variant - The variant string from the markdown
 * @returns Normalized ButtonVariant
 */
function normalizeVariant(variant: string): ButtonVariant {
  const normalized = variant.toLowerCase().trim();

  // Map aliases to canonical variants
  const aliasMap: Record<string, ButtonVariant> = {
    'accept': 'success',
    'yes': 'success',
    'reject': 'danger',
    'no': 'danger',
    'maybe': 'warning',
  };

  return aliasMap[normalized] || (normalized as ButtonVariant) || 'default';
}

/**
 * Parses message content into segments of text, links, and buttons
 * 
 * @param content - The raw message content to parse
 * @returns Array of parsed content segments
 */
function parseContent(content: string): ContentSegment[] {
  const segments: ContentSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Reset regex state
  LINK_PATTERN.lastIndex = 0;

  while ((match = LINK_PATTERN.exec(content)) !== null) {
    // Add preceding text if any
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: content.slice(lastIndex, match.index)
      });
    }

    const fullLabel = match[1];
    const url = match[2];

    // Check if this is a button (btn: prefix)
    if (fullLabel.startsWith('btn:')) {
      const buttonContent = fullLabel.slice(4); // Remove 'btn:' prefix

      // Check for variant syntax: Label:variant
      const lastColonIndex = buttonContent.lastIndexOf(':');

      if (lastColonIndex > 0) {
        // Has variant
        const label = buttonContent.slice(0, lastColonIndex);
        const variantStr = buttonContent.slice(lastColonIndex + 1);
        segments.push({
          type: 'button',
          label,
          url,
          variant: normalizeVariant(variantStr)
        });
      } else {
        // No variant, use default
        segments.push({
          type: 'button',
          label: buttonContent,
          url,
          variant: 'default'
        });
      }
    } else {
      segments.push({
        type: 'link',
        label: fullLabel,
        url
      });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text if any
  if (lastIndex < content.length) {
    segments.push({
      type: 'text',
      content: content.slice(lastIndex)
    });
  }

  return segments;
}

/**
 * True when a text segment is only whitespace between two parsed button tokens.
 * Model output often inserts blank lines between buttons; skip those for layout.
 */
function isWhitespaceOnlyBetweenButtons(
  segments: ContentSegment[],
  index: number
): boolean {
  const segment = segments[index];
  if (segment.type !== 'text' || segment.content.trim() !== '') {
    return false;
  }

  const previous = segments[index - 1];
  const next = segments[index + 1];
  return previous?.type === 'button' && next?.type === 'button';
}

/**
 * Renders a link segment as an anchor element with vertical separation
 */
const LinkSegment: React.FC<{
  label: string;
  url: string;
  isCustomerMessage: boolean;
}> = ({ label, url, isCustomerMessage }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className={`chatbot-message-link break-words max-w-full underline underline-offset-2 hover:opacity-80 transition-opacity ${isCustomerMessage
        ? 'decoration-primary-foreground/50 hover:decoration-primary-foreground'
        : 'decoration-card-foreground/50 hover:decoration-card-foreground'
      }`}
    style={{ display: 'block', marginTop: '12px', marginBottom: '12px' }}
  >
    {label}
  </a>
);

/**
 * Creates custom components for react-markdown to maintain consistent styling
 * 
 * Note: We avoid spreading props from react-markdown as they include a `node` 
 * property that's incompatible with standard HTML element types.
 * 
 * @param isCustomerMessage - Whether this is in a customer message bubble
 * @param interactive - When false (typing), links render as plain text for stable layout
 * @returns Components configuration for react-markdown
 */
function createMarkdownComponents(
  isCustomerMessage: boolean,
  interactive: boolean = true
): Components {
  return {
    // Customize anchor elements to use existing link styling with proper wrapping
    a: ({ href, children }) => {
      if (!interactive) {
        return <span className="chatbot-message-link-text">{children}</span>;
      }

      const url = href || '';
      // Extract text content from children (handles React nodes)
      const getTextContent = (nodeContent: React.ReactNode): string => {
        if (typeof nodeContent === 'string') return nodeContent;
        if (typeof nodeContent === 'number') return String(nodeContent);
        if (Array.isArray(nodeContent)) return nodeContent.map(getTextContent).join('');
        if (React.isValidElement(nodeContent)) {
          const linkProps = nodeContent.props as { children?: React.ReactNode };
          if (linkProps.children) {
            return getTextContent(linkProps.children);
          }
        }
        return '';
      };
      const label = getTextContent(children) || url;

      return (
        <LinkSegment
          label={label}
          url={url}
          isCustomerMessage={isCustomerMessage}
        />
      );
    },
    // Style strong elements (bold text)
    strong: ({ children }) => (
      <strong className="chatbot-message-bold font-bold">
        {children}
      </strong>
    ),
    // Style emphasis elements (italic text)
    em: ({ children }) => (
      <em className="chatbot-message-italic italic">
        {children}
      </em>
    ),
    // Style unordered lists - list-outside keeps wrapped text aligned under content
    ul: ({ children }) => (
      <ul className="chatbot-message-list list-disc list-outside pl-5 mt-1 mb-2.5">
        {children}
      </ul>
    ),
    // Style ordered lists - list-outside keeps wrapped text aligned under content
    ol: ({ children }) => (
      <ol className="chatbot-message-list chatbot-message-list-ordered list-decimal list-outside pl-5 mt-1 mb-2.5">
        {children}
      </ol>
    ),
    // Style list items
    li: ({ children }) => (
      <li className="chatbot-message-list-item leading-normal pl-1">
        {children}
      </li>
    ),
    // Style paragraphs - skip empty paragraphs
    p: ({ children }) => {
      // Skip rendering empty paragraphs (just whitespace)
      const childrenArray = React.Children.toArray(children);
      const hasContent = childrenArray.some(child => {
        if (typeof child === 'string') return child.trim().length > 0;
        return true;
      });
      if (!hasContent) return null;

      // Check if paragraph contains only links (or links with minimal text)
      const textNodes = childrenArray.filter(child => typeof child === 'string');
      const textContent = textNodes.join('').trim();
      const hasOnlyLinks = textContent.length === 0 || /^[, ]*$/.test(textContent);

      // If paragraph has only links, add spacing between them
      const paragraphClass = hasOnlyLinks
        ? "chatbot-message-paragraph my-0 [&+p]:mt-1.5 first:mt-0 [&>a]:block [&>a]:my-4 [&>a:first-child]:mt-0 [&>a:last-child]:mb-0"
        : "chatbot-message-paragraph my-0 [&+p]:mt-1.5 first:mt-0";

      return (
        <p className={paragraphClass}>
          {children}
        </p>
      );
    },
    // Style tables
    table: ({ children }) => (
      <div className="chatbot-message-table-container overflow-x-auto my-3">
        <table className="chatbot-message-table min-w-full border-collapse text-sm">
          {children}
        </table>
      </div>
    ),
    // Style table header
    thead: ({ children }) => (
      <thead className="chatbot-message-table-head border-b-2 border-current/30">
        {children}
      </thead>
    ),
    // Style table body
    tbody: ({ children }) => (
      <tbody className="chatbot-message-table-body divide-y divide-current/10">
        {children}
      </tbody>
    ),
    // Style table rows
    tr: ({ children }) => (
      <tr className="chatbot-message-table-row">
        {children}
      </tr>
    ),
    // Style table header cells - bold with border
    th: ({ children }) => (
      <th className="chatbot-message-table-header px-3 py-2 text-left font-bold whitespace-nowrap">
        {children}
      </th>
    ),
    // Style table data cells
    td: ({ children }) => (
      <td className="chatbot-message-table-cell px-3 py-2 text-left">
        {children}
      </td>
    ),
    // Style blockquotes - indented with border
    blockquote: ({ children }) => (
      <blockquote className="chatbot-message-blockquote border-l-4 border-current/30 pl-4 my-2 italic opacity-90">
        {children}
      </blockquote>
    ),
    // Style strikethrough text
    del: ({ children }) => (
      <del className="chatbot-message-strikethrough line-through opacity-60">
        {children}
      </del>
    ),
    // Style horizontal rules
    hr: () => (
      <hr className="chatbot-message-hr my-4 border-t border-current/20" />
    ),
    // Style code blocks — skip empty fenced blocks (malformed LLM output)
    pre: ({ children }) => {
      if (isBlankCodeBlock(children)) {
        return null;
      }

      return (
        <pre className="chatbot-message-code-block my-2 p-3 bg-black/10 rounded-md overflow-x-auto text-sm">
          {children}
        </pre>
      );
    },
    // Style inline code
    code: ({ children, className }) => {
      // Check if this is inside a pre (code block) or inline
      const isCodeBlock = className?.includes('language-');
      if (isCodeBlock) {
        return <code className={className}>{children}</code>;
      }
      return (
        <code className="chatbot-message-code px-1.5 py-0.5 bg-black/10 rounded text-sm font-mono">
          {children}
        </code>
      );
    },
  };
}

/**
 * Button variant style configuration
 */
interface ButtonStyles {
  className: string;
  style?: React.CSSProperties;
}

/**
 * Gets the CSS classes and inline styles for a button variant
 * 
 * @param variant - The button variant
 * @param isCustomerMessage - Whether this is in a customer message bubble
 * @returns Button styles object with className and optional inline styles
 */
function getButtonVariantStyles(variant: ButtonVariant, isCustomerMessage: boolean): ButtonStyles {
  // For customer messages, use semi-transparent styling
  if (isCustomerMessage) {
    return {
      className: 'chatbot-message-button bg-primary-foreground/20 text-primary-foreground border border-primary-foreground/30 hover:bg-primary-foreground/30'
    };
  }

  // For agent messages, use CSS variables for themeable colors
  switch (variant) {
    case 'success':
    case 'accept':
    case 'yes':
      return {
        className: 'chatbot-message-button chatbot-message-button-success border hover:opacity-90 bg-[rgb(var(--chatbot-btn-success-bg))] text-[rgb(var(--chatbot-btn-success-text))] border-[rgb(var(--chatbot-btn-success-border))]'
      };

    case 'danger':
    case 'reject':
    case 'no':
      return {
        className: 'chatbot-message-button chatbot-message-button-danger border hover:opacity-90 bg-[rgb(var(--chatbot-btn-danger-bg))] text-[rgb(var(--chatbot-btn-danger-text))] border-[rgb(var(--chatbot-btn-danger-border))]'
      };

    case 'warning':
    case 'maybe':
      return {
        className: 'chatbot-message-button chatbot-message-button-warning border hover:opacity-90 bg-[rgb(var(--chatbot-btn-warning-bg))] text-[rgb(var(--chatbot-btn-warning-text))] border-[rgb(var(--chatbot-btn-warning-border))]'
      };

    case 'info':
      return {
        className: 'chatbot-message-button chatbot-message-button-info border hover:opacity-90 bg-[rgb(var(--chatbot-btn-info-bg))] text-[rgb(var(--chatbot-btn-info-text))] border-[rgb(var(--chatbot-btn-info-border))]'
      };

    case 'neutral':
      return {
        className: 'chatbot-message-button chatbot-message-button-neutral border hover:opacity-90 bg-[rgb(var(--chatbot-btn-neutral-bg))] text-[rgb(var(--chatbot-btn-neutral-text))] border-[rgb(var(--chatbot-btn-neutral-border))]'
      };

    case 'primary':
      return {
        className: 'chatbot-message-button bg-primary text-primary-foreground border border-primary/20 hover:bg-primary/90'
      };

    case 'default':
    default:
      return {
        className: 'chatbot-message-button bg-accent text-accent-foreground border border-accent-foreground/20 hover:bg-accent/80 shadow-sm'
      };
  }
}

/**
 * Renders a button segment as a styled button element
 */
const ButtonSegment: React.FC<{
  label: string;
  url: string;
  variant: ButtonVariant;
  onClick?: (url: string, label: string, variant?: ButtonVariant) => void;
  isCustomerMessage: boolean;
}> = ({ label, url, variant, onClick, isCustomerMessage }) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();

    if (onClick) {
      onClick(url, label, variant);
    } else {
      // Default behavior: open URL in new tab
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const { className: variantClassName, style: variantStyle } = getButtonVariantStyles(variant, isCustomerMessage);

  return (
    <div className="block mt-2 mb-2.5 first:mt-0 last:mb-0">
      <button
        onClick={handleClick}
        data-variant={variant}
        className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md 
          transition-all duration-200 ease-in-out
          hover:scale-[1.02] active:scale-[0.98]
          ${variantClassName}`}
        style={variantStyle}
      >
        {label}
      </button>
    </div>
  );
};

/**
 * MessageContent - Parses and renders rich content in chat messages
 * 
 * @example
 * ```tsx
 * // Basic buttons and links
 * <MessageContent 
 *   content="Check out [our docs](https://example.com) or [btn:Sign Up](https://signup.com)"
 *   onButtonClick={(url, label) => console.log(`Button clicked: ${label}`)}
 * />
 * 
 * // Variant buttons for actions
 * <MessageContent 
 *   content="Would you like to proceed? [btn:Yes:accept](action:confirm) [btn:No:reject](action:cancel) [btn:Maybe Later:neutral](action:defer)"
 * />
 * ```
 */
export const MessageContent = memo(function MessageContent({
  content,
  onButtonClick,
  isCustomerMessage = false,
  isTyping = false,
}: MessageContentProps) {
  // Parse content into segments (memoized for performance).
  // While typing, skip link/button parsing so partial tokens stay stable.
  const segments = useMemo(
    () => (isTyping ? [{ type: 'text' as const, content }] : parseContent(content)),
    [content, isTyping]
  );

  // Create markdown components (memoized for performance)
  const markdownComponents = useMemo(
    () => createMarkdownComponents(isCustomerMessage, !isTyping),
    [isCustomerMessage, isTyping]
  );

  const renderMarkdownBlock = (textContent: string, key?: number): React.ReactNode => {
    if (!hasMarkdownFormatting(textContent)) {
      if (containsLineBreak(textContent)) {
        return key !== undefined ? (
          <span key={key} className="whitespace-pre-line">
            {textContent}
          </span>
        ) : (
          <span className="whitespace-pre-line">{textContent}</span>
        );
      }
      return key !== undefined ? <span key={key}>{textContent}</span> : <>{textContent}</>;
    }

    const processedContent = normalizeMarkdownForChat(textContent);
    return (
      <ReactMarkdown
        key={key}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight, rehypeRaw]}
        components={markdownComponents}
      >
        {preserveLineBreaks(processedContent)}
      </ReactMarkdown>
    );
  };

  // If no links or buttons found, check if content has markdown formatting
  if (segments.length === 1 && segments[0].type === 'text') {
    return renderMarkdownBlock(segments[0].content);
  }

  return (
    <>
      {segments.map((segment, index) => {
        switch (segment.type) {
          case 'text': {
            if (isWhitespaceOnlyBetweenButtons(segments, index)) {
              return null;
            }
            return renderMarkdownBlock(segment.content, index);
          }

          case 'link':
            return (
              <LinkSegment
                key={index}
                label={segment.label}
                url={segment.url}
                isCustomerMessage={isCustomerMessage}
              />
            );

          case 'button':
            return (
              <ButtonSegment
                key={index}
                label={segment.label}
                url={segment.url}
                variant={segment.variant}
                onClick={onButtonClick}
                isCustomerMessage={isCustomerMessage}
              />
            );

          default:
            return null;
        }
      })}
    </>
  );
});

export type { MessageContentProps, ContentSegment };
